/**
 * Uma saída da versão (E159.F3, ADR-0109 §2.7): o nome e o papel dela, a situação da conferência,
 * o estado, o artefato conferido (etapa, versão, rodada e data), o que ela precisa agora, os
 * apontamentos e os critérios por expansão. Nada é recalculado aqui.
 */
import { CorrigirNaMesa } from '@/components/DocumentoDaMesa/CorrigirNaMesa';
import { formatarDataHora } from '@/components/Portal/Caso/rotulos';
import { ptBR } from '@/i18n/pt-BR';
import type { EstadoDaConferencia, SaidaNaConferenciaData } from '@/lib/conferenciaDasSaidas';

import { ApontamentoDaSaida } from './ApontamentoDaSaida';
import { CriteriosConferidos } from './CriteriosConferidos';

const t = ptBR.conferenciaDasSaidas;
const motivos: Record<string, string> = ptBR.orientacoes.equipe.motivos;

const COR_DO_ESTADO: Record<EstadoDaConferencia, string> = {
  aprovado: 'border-success-500 bg-success-50 text-success-700',
  corrigir: 'border-warning-500 bg-warning-50 text-warning-700',
  bloqueado: 'border-danger-500 bg-danger-50 text-danger-700',
};

function Artefato({ saida }: { saida: SaidaNaConferenciaData }) {
  const a = saida.artefato;
  if (a === null) return null;
  return (
    <p className="text-caption text-fg-muted" data-testid="artefato-conferido">
      {t.artefato
        .replace('{etapa}', t.etapa[a.etapa])
        .replace('{versao}', String(a.versao))
        .replace('{rodada}', String(a.rodada))
        .replace('{data}', formatarDataHora(a.criada_em))}
    </p>
  );
}

export function SaidaNaConferencia({
  saida,
  caseId,
}: {
  saida: SaidaNaConferenciaData;
  caseId: string;
}) {
  const nome = t.saida[saida.kind];
  const conferencia = saida.conferencia;
  const titulo = `saida-${saida.kind}`;
  return (
    <article
      className="border-t border-border py-sc-4 first:border-t-0"
      aria-labelledby={titulo}
      data-testid={`saida-${saida.kind}`}
      data-situacao={saida.situacao}
      data-pendencia={saida.pendencia}
    >
      <div className="flex flex-wrap items-start justify-between gap-sc-2">
        <div>
          <h3 id={titulo} className="text-body-md font-semibold text-fg">
            {nome.nome}
          </h3>
          <p className="max-w-prose text-body-sm text-fg-muted">{nome.descricao}</p>
        </div>
        {conferencia !== null ? (
          <span
            className={`border px-sc-2 py-0.5 text-caption ${COR_DO_ESTADO[conferencia.estado]}`}
            data-testid="estado-da-conferencia"
          >
            {t.estado[conferencia.estado]}
          </span>
        ) : null}
      </div>
      <p className="mt-sc-2 text-body-sm text-fg">{t.situacao[saida.situacao]}</p>
      <Artefato saida={saida} />
      <p className="mt-sc-1 text-body-sm text-fg" data-testid="pendencia">
        {t.pendencia[saida.pendencia]}
      </p>
      {/* E171.F3 (ADR-0122 §2.7): a redação da saída se corrige na mesa (E202.F3). */}
      <CorrigirNaMesa caseId={caseId} saida={saida.kind === 'laudo' ? 'exame' : 'riscos'} />
      {saida.motivo !== null ? (
        <p className="mt-sc-1 text-caption text-fg-muted" data-testid="motivo-da-saida">
          {t.motivoDaSaida.replace(
            '{motivo}',
            motivos[saida.motivo] ?? ptBR.orientacoes.equipe.motivoDesconhecido,
          )}
        </p>
      ) : null}
      {conferencia !== null ? (
        <section className="mt-sc-3" aria-label={t.apontamentos}>
          {conferencia.apontamentos.length === 0 ? (
            <p className="text-caption text-fg-muted">{t.semApontamentos}</p>
          ) : (
            conferencia.apontamentos.map((a) => (
              <ApontamentoDaSaida key={a.id} apontamento={a} caseId={caseId} />
            ))
          )}
          <CriteriosConferidos criterios={conferencia.criterios} />
        </section>
      ) : null}
    </article>
  );
}
